import React, { createContext, useState, useEffect, useContext } from "react";
import { initializeApp } from "firebase/app";
import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithRedirect,
  getRedirectResult,
  sendEmailVerification,
  updateProfile,
  EmailAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  deleteUser,
} from "firebase/auth";
import { getFirestore, doc, setDoc, getDoc, deleteDoc } from "firebase/firestore";
import { POLICY_VERSION } from "../legal/legalMeta";
import { deleteLinkedRecords, clearLocalUserData } from "../utils/userData";
import { isPremiumActive } from "../utils/premium";

// Firebase configuration - Replace with your config
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const isFirebaseConfigured = Object.values(firebaseConfig).every(
  (v) => typeof v === "string" && v.trim().length > 0,
);

// Initialize Firebase (guarded so builds/tests don't crash without env)
const app = isFirebaseConfigured ? initializeApp(firebaseConfig) : null;
const auth = app ? getAuth(app) : null;
const db = app ? getFirestore(app) : null;

const AuthContext = createContext();

// Version of the legal documents the user accepted (set in src/legal/legalMeta.js).
export { POLICY_VERSION };
const PENDING_CONSENT_KEY = "noryq-pending-consent";

// Proof of what the user agreed to and when (kept on their user record).
// method: "checkbox" (explicit tick) or "notice" (clicked Google sign-in under
// a visible 'by continuing you agree' notice).
export const buildConsentRecord = (method) => ({
  acceptedAt: new Date().toISOString(),
  termsVersion: POLICY_VERSION,
  privacyVersion: POLICY_VERSION,
  ageConfirmed18: true,
  method,
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);

  useEffect(() => {
    if (!auth || !db) {
      setUser(null);
      setIsPremium(false);
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);

      if (user) {
        // Check premium status from Firestore
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          setIsPremium(isPremiumActive(userDoc.data()));
        }
      } else {
        setIsPremium(false);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Handle Google redirect result (for PWA)
  useEffect(() => {
    const handleRedirectResult = async () => {
      if (!auth || !db) return;
      try {
        const result = await getRedirectResult(auth);

        if (result) {
          console.log("✅ Google redirect successful:", result.user.email);

          // Check if user document exists
          const userDoc = await getDoc(doc(db, "users", result.user.uid));

          if (!userDoc.exists()) {
            // Consent was captured before the redirect; pick it up here.
            let pendingConsent = null;
            try {
              pendingConsent = JSON.parse(
                localStorage.getItem(PENDING_CONSENT_KEY) || "null",
              );
              localStorage.removeItem(PENDING_CONSENT_KEY);
            } catch (_) {}

            // Create document for new Google user
            await setDoc(doc(db, "users", result.user.uid), {
              displayName: result.user.displayName,
              email: result.user.email,
              photoURL: result.user.photoURL,
              isPremium: false,
              createdAt: new Date().toISOString(),
              likedMovies: [], // ✅ ADD THIS
              consent: pendingConsent,
            });
            console.log("✅ New user document created");
          }
        }
      } catch (error) {
        console.error("❌ Redirect result error:", error);

        // Show helpful error message
        if (error.code === "auth/unauthorized-domain") {
          console.error("⚠️ Domain not authorized in Firebase Console");
          console.error(
            "Add your domain: Firebase Console → Authentication → Settings → Authorized domains",
          );
        }
      }
    };

    handleRedirectResult();
  }, []);

  // Sign up with email/password
  const signUp = async (email, password, displayName, consent) => {
    if (!auth || !db) throw new Error("Firebase is not configured");
    if (!consent?.ageConfirmed18) {
      throw new Error(
        "You must confirm you are 18+ and accept the Terms and Privacy Policy to create an account.",
      );
    }
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password,
    );
    const user = userCredential.user;

    // Update profile
    await updateProfile(user, { displayName });

    // Create Firestore document
    await setDoc(doc(db, "users", user.uid), {
      displayName,
      email,
      isPremium: false,
      createdAt: new Date().toISOString(),
      likedMovies: [], // ✅ ADD THIS
      consent,
    });

    return user;
  };

  const sendVerificationEmail = async (userParam) => {
    if (!auth) throw new Error("Firebase is not configured");
    try {
      const targetUser = userParam || auth.currentUser;

      if (!targetUser) {
        throw new Error("No user to send email to");
      }

      if (targetUser.emailVerified) {
        console.log("Email already verified");
        return;
      }

      await sendEmailVerification(targetUser, {
        url: window.location.origin,
        handleCodeInApp: false,
      });

      console.log("✅ Verification email sent to:", targetUser.email);
      return true;
    } catch (error) {
      console.error("❌ Verification email error:", error);
      throw error;
    }
  };

  // Sign in with email/password
  const signIn = async (email, password) => {
    if (!auth) throw new Error("Firebase is not configured");
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password,
    );
    const user = userCredential.user;

    if (!user.emailVerified) {
      console.warn("Email not verified:", user.email);
    }

    return user;
  };

  const resetPassword = async (email) => {
    if (!auth) throw new Error("Firebase is not configured");
    await sendPasswordResetEmail(auth, email, {
      url: window.location.origin,
      handleCodeInApp: false,
    });
  };

  // Sign in with Google (PWA-compatible)
  const signInWithGoogle = async (consent) => {
    if (!auth || !db) throw new Error("Firebase is not configured");
    if (!consent?.ageConfirmed18) {
      throw new Error(
        "You must confirm you are 18+ and accept the Terms and Privacy Policy to continue.",
      );
    }
    const provider = new GoogleAuthProvider();

    try {
      const isPWA =
        window.matchMedia("(display-mode: standalone)").matches ||
        window.navigator.standalone ||
        document.referrer.includes("android-app://");

      if (isPWA) {
        console.log("🔄 PWA detected - using redirect auth");
        try {
          localStorage.setItem(PENDING_CONSENT_KEY, JSON.stringify(consent));
        } catch (_) {}
        await signInWithRedirect(auth, provider);
        return null;
      } else {
        console.log("🪟 Browser detected - using popup auth");
        const result = await signInWithPopup(auth, provider);

        const userDoc = await getDoc(doc(db, "users", result.user.uid));

        if (!userDoc.exists()) {
          await setDoc(doc(db, "users", result.user.uid), {
            displayName: result.user.displayName,
            email: result.user.email,
            photoURL: result.user.photoURL,
            isPremium: false,
            createdAt: new Date().toISOString(),
            likedMovies: [], // ✅ ADD THIS
            consent,
          });
        }

        return result;
      }
    } catch (error) {
      console.error("❌ Google sign-in error:", error);
      throw error;
    }
  };

  // Sign out
  const logout = async () => {
    try {
      if (!auth) return;
      await signOut(auth);
    } catch (error) {
      throw error;
    }
  };

  // Update user data in Firestore
  const updateUserData = async (data) => {
    if (!user) return;
    if (!db) throw new Error("Firebase is not configured");

    try {
      await setDoc(doc(db, "users", user.uid), data, { merge: true });
    } catch (error) {
      console.error("Error updating user data:", error);
      throw error;
    }
  };

  // Get user data from Firestore
  const getUserData = async () => {
    if (!user) return null;
    if (!db) throw new Error("Firebase is not configured");

    try {
      const userDoc = await getDoc(doc(db, "users", user.uid));
      return userDoc.exists() ? userDoc.data() : null;
    } catch (error) {
      console.error("Error getting user data:", error);
      throw error;
    }
  };

  const reloadUserData = async () => {
    if (!user) return;
    if (!db) return;

    try {
      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setIsPremium(isPremiumActive(userData));
      }
    } catch (error) {
      console.error("Error reloading:", error);
    }
  };

  // Permanently delete the signed-in user's account and data (erasure right).
  // Order matters: verify identity -> delete data -> delete login.
  const deleteAccount = async (password) => {
    if (!auth || !db || !auth.currentUser) {
      throw new Error("You need to be signed in to delete your account.");
    }
    const current = auth.currentUser;
    const providers = (current.providerData || []).map((p) => p.providerId);

    // 1) Re-verify identity (also protects against a hijacked open session).
    try {
      if (providers.includes("password")) {
        if (!password) throw new Error("Please enter your password to confirm.");
        await reauthenticateWithCredential(
          current,
          EmailAuthProvider.credential(current.email, password),
        );
      } else if (providers.includes("google.com")) {
        await reauthenticateWithPopup(current, new GoogleAuthProvider());
      }
    } catch (e) {
      if (
        e.code === "auth/wrong-password" ||
        e.code === "auth/invalid-credential"
      ) {
        throw new Error("That password is not correct.");
      }
      if (e.code === "auth/popup-blocked" || e.code === "auth/popup-closed-by-user") {
        throw new Error(
          "Google confirmation was blocked or closed. Allow pop-ups and try again.",
        );
      }
      throw e;
    }

    // 2) Delete data first, while we are still authenticated.
    await deleteLinkedRecords(db, current.uid);
    await deleteDoc(doc(db, "users", current.uid));

    // 3) Delete the login itself.
    try {
      await deleteUser(current);
    } catch (e) {
      throw new Error(
        "Your data was deleted, but removing your sign-in failed. Please email noryq.official@gmail.com and we will finish it.",
      );
    }

    // 4) Clear what this browser kept.
    clearLocalUserData();
    setIsPremium(false);
  };

  // ✅ ═══════════════════════════════════════════════════════════
  // ✅ NEW: LIKED MOVIES FUNCTIONS
  // ✅ ═══════════════════════════════════════════════════════════

  const addToLikedMovies = async (movie) => {
    if (!user) {
      throw new Error("Must be logged in to like movies");
    }

    try {
      const userData = await getUserData();
      const likedMovies = userData?.likedMovies || [];

      // Check if already liked
      const alreadyLiked = likedMovies.some(
        (m) => m.title.toLowerCase() === movie.title.toLowerCase(),
      );

      if (alreadyLiked) {
        console.log("Movie already liked");
        return { success: false, message: "Already in liked movies" };
      }

      // Add movie with timestamp
      const likedMovie = {
        ...movie,
        likedAt: new Date().toISOString(),
      };

      const updatedLikedMovies = [...likedMovies, likedMovie];

      await updateUserData({ likedMovies: updatedLikedMovies });

      console.log("✅ Movie added to liked:", movie.title);
      return { success: true, likedMovies: updatedLikedMovies };
    } catch (error) {
      console.error("Error adding to liked movies:", error);
      throw error;
    }
  };

  const removeFromLikedMovies = async (movieTitle) => {
    if (!user) return;

    try {
      const userData = await getUserData();
      const likedMovies = userData?.likedMovies || [];

      const updatedLikedMovies = likedMovies.filter(
        (m) => m.title.toLowerCase() !== movieTitle.toLowerCase(),
      );

      await updateUserData({ likedMovies: updatedLikedMovies });

      console.log("✅ Movie removed from liked:", movieTitle);
      return { success: true, likedMovies: updatedLikedMovies };
    } catch (error) {
      console.error("Error removing from liked movies:", error);
      throw error;
    }
  };

  const isMovieLiked = async (movieTitle) => {
    if (!user) return false;

    try {
      const userData = await getUserData();
      const likedMovies = userData?.likedMovies || [];

      return likedMovies.some(
        (m) => m.title.toLowerCase() === movieTitle.toLowerCase(),
      );
    } catch (error) {
      console.error("Error checking if movie is liked:", error);
      return false;
    }
  };

  const getLikedMovies = async () => {
    if (!user) return [];

    try {
      const userData = await getUserData();
      return userData?.likedMovies || [];
    } catch (error) {
      console.error("Error getting liked movies:", error);
      return [];
    }
  };

  // ✅ ═══════════════════════════════════════════════════════════

  const value = {
    user,
    isPremium,
    setIsPremium,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    logout,
    updateUserData,
    getUserData,
    reloadUserData,
    sendVerificationEmail,
    resetPassword,
    deleteAccount,
    // ✅ NEW: Liked movies functions
    addToLikedMovies,
    removeFromLikedMovies,
    isMovieLiked,
    getLikedMovies,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export { auth, db };
